import NotebookRecord from "models/view-models/notebook-record";
import NotebookService from "utilities/services/notebooks/notebook-service";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import { ResultErrorRecord } from "@rsm-hcd/javascript-core";
import { t } from "i18next";
import { useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import useNotebooksRedirects from "utilities/hooks/domain/notebooks/use-notebooks-redirects";

export const DEFAULT_ERROR_KEY = "Error.NotebookDeletionFailed";
const DEFAULT_ERROR_MESSAGE = t("notebook-error-deleteNotebook");

interface UseDeleteNotebookOptions {
    notebook?: NotebookRecord;
    onSuccess?: () => void;
}

/**
 * Custom hook to wrap up the API calls to delete Notebooks.
 */
export function useDeleteNotebook(options: UseDeleteNotebookOptions) {
    const { notebook, onSuccess } = options;
    const { delete: deleteNotebookApi } = NotebookService.useDelete();
    const { removeNotebook } = useNotebooksAtom();
    const [errors, setErrors] = useState<ResultErrorRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const { redirectToDefaultNotebookAfterDelete } = useNotebooksRedirects();

    const deleteNotebook = async () => {
        setLoading(true);
        setErrors([]);
        try {
            if (notebook?.id == null) {
                throw Error;
            }

            const response = await deleteNotebookApi(notebook.id);
            if (response.result?.hasErrors()) {
                if (CollectionUtils.isEmpty(response.result?.errors)) {
                    throw Error;
                }

                setErrors(response.result?.errors);
                return;
            }
            redirectToDefaultNotebookAfterDelete(notebook.id);
            removeNotebook(notebook.id);

            onSuccess?.();
        } catch (error) {
            setErrors([
                new ResultErrorRecord({
                    key: DEFAULT_ERROR_KEY,
                    message: DEFAULT_ERROR_MESSAGE,
                }),
            ]);
        } finally {
            setLoading(false);
        }
    };

    return { deleteNotebook, loading, errors: errors };
}
